import React, { startTransition, useCallback, useEffect, useState } from 'react';
import { LibraryItem } from '../../../pages/library/components/library-list/types';
import { NWClient } from '../../../client/NWClient';
import { toast } from 'react-toastify';
import { ROUTES } from '../../../common/constants/routes';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AIContractModel } from '../../../models/ai.contract.model';
import { addDocumentContract } from '../../../slices/document-contracts/documentContractsSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import {
  fetchBusinessProfileById,
  fetchBusinessProfileContactById,
} from '../../../slices/business-profile/businessProfileSlice';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { showMessage } from '../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { Helpers } from '../../helpers/helpers';
import { RootState } from '../../../store';
import { sanitizeRedirectUrl } from '../../helpers/sanitize';
import { useErrorMessage } from '../../hooks/useErrorMessage';

export const useContractsGenerator = (category: string) => {
  const currentUser = useAppSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const location = useLocation();
  const path = location.pathname;
  const company = useAppSelector(selectAllCompanies)[0];
  const employeesLoading = useAppSelector((state: RootState) => state.employees.status);
  const customersLoading = useAppSelector((state: RootState) => state.customers.status);
  const suppliersLoading = useAppSelector((state: RootState) => state.suppliers.status);
  const businessDetailsLoading = useAppSelector((state: RootState) => state.businessProfile.status);
  const [generatorOpened, setGeneratorOpened] = useState<boolean>(false);
  const [analysisLoading, setAnalysisLoading] = useState<boolean>(false);
  const [secondPartyCategory, setSecondPartyCategory] = useState<string>(category || '');
  const [secondParty, setSecondParty] = useState<string>('noSecondParty');
  const [checkingBusinessProfile, setCheckingBusinessProfile] = useState<boolean>(true);
  const [placeholders, setPlaceholders] = useState<{
    [key: string]: string;
  }>({});
  const [step, setStep] = useState<number>(1);
  const [aiContract, setAIContract] = useState<AIContractModel>(null as unknown as AIContractModel);
  const handleGenerateTemplate = (
    libraryItem: LibraryItem,
    data: {
      user: number;
      document_library?: number;
      document_template?: number;
      company_id: number;
      secondary_id?: number;
      category: string;
    }
  ) => {
    setAnalysisLoading(true);
    NWClient.post('ai-contract', data)
      .then((response) => {
        const res = response as AIContractModel;
        //toast.success(t('messages.contractGenerated'), { theme: 'colored' });
        setAIContract(res);
        setPlaceholders(res.placeholder_matches);
        setStep(2);
        setAnalysisLoading(false);
        localStorage.setItem('AIContractFail', 'false');
      })
      .catch((error) => {
        setAnalysisLoading(false);
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.userNotAssociatedWithCompany')
        ) {
          navigate(
            `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
          );
        } else {
          if (localStorage.getItem('AIContractFail') === 'true') {
            toast.error(getErrorMessage(error));
          } else {
            localStorage.setItem('AIContractFail', 'true');
            toast.error(t('messages.firstAIContractFail'));
          }
        }
      });
  };

  const companyIdRef = React.useRef(company?.id);

  const retrieveEmployeesData = useCallback(() => {
    if (employeesLoading === 'idle') {
      dispatch(fetchEmployees({ company_id: companyIdRef.current ?? 0 }));
    }
  }, [dispatch, employeesLoading]);

  const retrieveCustomersData = useCallback(() => {
    if (customersLoading === 'idle') {
      dispatch(fetchCustomers({ company_id: companyIdRef.current ?? 0, page_size: 100 }));
    }
  }, [dispatch, customersLoading]);

  const retrieveSuppliersData = useCallback(() => {
    if (suppliersLoading === 'idle') {
      dispatch(fetchSuppliers({ company_id: companyIdRef.current ?? 0, page_size: 100 }));
    }
  }, [dispatch, suppliersLoading]);

  const retrieveBusinessDetailsData = useCallback(() => {
    if (businessDetailsLoading === 'idle') {
      dispatch(fetchBusinessProfileContactById({ id: companyIdRef.current ?? 0 }));
    }
  }, [dispatch, businessDetailsLoading]);

  const getProfileLabelsProp = () => {
    if (aiContract?.category === 'employment') {
      return 'employees';
    } else if (aiContract?.category === 'business') {
      return 'business_profile_contacts';
    } else {
      return aiContract?.category;
    }
  };
  const detectDocumentProfileRoute = () => {
    switch (secondPartyCategory) {
      case 'employment':
        return ROUTES.EMPLOYEE_PROFILES;
      case 'customers':
        return ROUTES.CUSTOMER_PROFILES;
      case 'suppliers':
        return ROUTES.SUPPLIER_PROFILES;
      case 'business':
        return ROUTES.BUSINESS_PROFILE;
    }
  };
  const createDocumentFromTemplate = (libraryItem: LibraryItem) => {
    setAnalysisLoading(true);
    const data = {
      user: currentUser.id,
      contract_generation: aiContract.id,
      name:
        (libraryItem.name ? libraryItem.name : t('documentPage.headerDefault')) +
        ' ' +
        Helpers.timeStampToDateV4(new Date().toISOString()),
      category: aiContract?.category,
    };
    const contractData = { ...aiContract };
    delete (contractData as { id?: number }).id;
    contractData.placeholder_matches = placeholders;
    NWClient.put('ai-contract', Number(aiContract.id), contractData, true)
      .then(() => {
        dispatch(addDocumentContract({ data }))
          .unwrap()
          .then((res: any) => {
            NWClient.patch(`tag-document-contract/${res.id}`, null, {
              dc_workflows: [
                libraryItem.dl_workflows
                  ? libraryItem.dl_workflows[0]
                  : libraryItem.dt_workflows && libraryItem.dt_workflows.length > 0
                    ? libraryItem.dt_workflows[0]
                    : '',
              ],
            }).catch();
            NWClient.post('document-end-date-extraction', { document_contract_id: res.id });
            if (secondParty !== 'noSecondParty') {
              NWClient.patch(`document-contract/${Number(res.id)}/`, null, {
                [getProfileLabelsProp()]: [Number(secondParty)],
                user: currentUser?.id,
              }).finally(() => {
                toast.success(t('messages.contractCreated'));
                //navigate(ROUTES.DOCUMENT_VAULT + `?activeTab=${secondPartyCategory}`);
                navigate(
                  detectDocumentProfileRoute() +
                    `${
                      secondPartyCategory !== 'business'
                        ? '/' +
                          secondParty +
                          `?workflow=${
                            libraryItem[
                              'dl_workflows' in libraryItem ? 'dl_workflows' : 'dt_workflows'
                            ]?.[0]
                          }`
                        : '?workflow=' +
                          libraryItem[
                            'dl_workflows' in libraryItem ? 'dl_workflows' : 'dt_workflows'
                          ]?.[0]
                    }`
                );
                //setAnalysisLoading(false);
              });
            } else {
              toast.success(t('messages.contractCreated'));
              navigate(ROUTES.DOCUMENT_VAULT + `?activeTab=${secondPartyCategory}`);
              setAnalysisLoading(false);
            }
          })
          .catch((error) => {
            setAnalysisLoading(false);
            if (
              error.errors?.length > 0 &&
              t('messages.exceededPlanLimits').includes(error.errors[0])
            ) {
              dispatch(showMessage(t('messages.monthLimitReached')));
            } else {
              toast.error(getErrorMessage(error));
            }
          });
      })
      .catch((error) => {
        setAnalysisLoading(false);
        if (
          error.errors?.length > 0 &&
          t('messages.exceededPlanLimits').includes(error.errors[0])
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          toast.error(getErrorMessage(error));
        }
      })
      .catch((error) => {
        setAnalysisLoading(false);
        toast.error(getErrorMessage(error));
      });
  };

  useEffect(() => {
    retrieveEmployeesData();
  }, [retrieveEmployeesData]);

  useEffect(() => {
    retrieveCustomersData();
  }, [retrieveCustomersData]);

  useEffect(() => {
    retrieveSuppliersData();
  }, [retrieveSuppliersData]);

  useEffect(() => {
    retrieveBusinessDetailsData();
  }, [retrieveBusinessDetailsData]);

  useEffect(() => {
    if (company) {
      dispatch(fetchBusinessProfileById({ id: company.id ?? 0 }))
        .unwrap()
        .finally(() => {
          setCheckingBusinessProfile(false);
        });
    } else {
      startTransition(() => {
        setCheckingBusinessProfile(false);
      });
    }
  }, [company, dispatch]);

  return {
    generatorOpened,
    setGeneratorOpened,
    analysisLoading,
    setAnalysisLoading,
    handleGenerateTemplate,
    secondParty,
    secondPartyCategory,
    setSecondParty,
    setSecondPartyCategory,
    step,
    setStep,
    setPlaceholders,
    placeholders,
    aiContract,
    createDocumentFromTemplate,
    checkingBusinessProfile,
  };
};
